import { useExperimentsEligibleProps } from '@seek/candidate-feature-flag';
import { useHubble } from '@seek/hubble';
import { useCallback, useMemo } from 'react';

/**
 * This hook just overwrites the trackEvent function from the Hubble SDK.
 * This is needed as we must pass additional tracking data for Launch Darkly.
 * https://github.com/SEEK-Jobs/metropolis/tree/master/packages/candidate-feature-flag#useexperimentseligibleprops
 */
export const useEnhancedHubble = () => {
  const hubble = useHubble();
  const {
    hubbleEligibleProps: { experiments, solMetadataTags },
  } = useExperimentsEligibleProps();

  hubble.updateOptions({ experiments });

  const trackEvent = useCallback(
    (
      eventName: Parameters<typeof hubble.trackEvent>[0],
      eventData: Parameters<typeof hubble.trackEvent>[1] = {},
      options?: Parameters<typeof hubble.trackEvent>[2],
    ) => {
      const existingSolMetadata = eventData?.solMetadata || {};
      const existingTags = (existingSolMetadata as any)?.tags || {};

      const enhancedEventData = {
        ...eventData,
        // Enrich eventData with solMetadataTags
        solMetadata: {
          ...existingSolMetadata,
          tags: {
            ...existingTags,
            ...solMetadataTags,
          },
        },
      };

      return hubble.trackEvent(
        eventName,
        enhancedEventData,
        ...(options !== undefined ? [options] : []),
      );
    },

    [hubble, solMetadataTags],
  );

  const enhancedHubble = useMemo(() => {
    // Create a new object that delegates to the original hubble
    const enhanced = Object.create(Object.getPrototypeOf(hubble));

    // Copy all properties and methods from hubble
    Object.setPrototypeOf(enhanced, hubble);
    Object.assign(enhanced, hubble);

    // Override only the trackEvent method
    enhanced.trackEvent = trackEvent;

    return enhanced as typeof hubble;
  }, [hubble, trackEvent]);

  return enhancedHubble;
};
